import { getSharedMeasureContext } from '@/lib/shapes/shape-text-measure'
import { CANVAS_FONT_MIN_PT, resolveCanvasFont } from '@/lib/text/canvas-font-metrics'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'

import {
  TEXT_ON_PATH_CAPACITY_SAMPLE_TEXT,
  TEXT_ON_PATH_FALLBACK_ADVANCE_RATIO,
} from '../constants/capacity-constants'

/**
 * Average glyph advance, in pt, of a representative sentence set in the given font at `pointSize`.
 *
 * Measured on the shared canvas context with the same font string the text engine draws with
 * (`resolveCanvasFont`), floored at `CANVAS_FONT_MIN_PT` for reliability and scaled back to the real
 * size like `measureGlyphAdvanceWidthPt`. Falls back to a ratio of the size when there is no canvas
 * (jsdom) or the measurement is unusable.
 */
export function measureAverageAdvancePt(
  pointSize: number,
  fontFamilyId: number | undefined,
  fontId: number | undefined,
  fonts?: ParagraphBlockFontLookup
): number {
  const fallback = TEXT_ON_PATH_FALLBACK_ADVANCE_RATIO * pointSize
  const ctx = getSharedMeasureContext()

  if (!ctx) return fallback

  ctx.font = resolveCanvasFont(pointSize, fontFamilyId, fontId, fonts)
  // The shared context is reused by the text engine, which sets its own letterSpacing per call; the
  // spacing is added separately here, so measure the bare advance.
  ctx.letterSpacing = '0px'

  const widthPx = ctx.measureText(TEXT_ON_PATH_CAPACITY_SAMPLE_TEXT).width

  if (!Number.isFinite(widthPx) || widthPx <= 0) return fallback

  const sizeUsed = Math.max(CANVAS_FONT_MIN_PT, pointSize)

  return (widthPx / sizeUsed / TEXT_ON_PATH_CAPACITY_SAMPLE_TEXT.length) * pointSize
}
